/* eslint-disable react-hooks/exhaustive-deps */
import {useEffect, useState} from 'react'
import {useNavigate, useParams} from 'react-router-dom'
import {ProizvodnjaModel} from '../../models/ProizvodnjaModel'
import {
    fetchProizvodnja,
    postojiLiNalogUpisan,
    saveProizvodnja,
    updateProizvodnja,
} from '../../api/proizvodnja'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import notifyToast from '../../components/Toast/Toast'
import {ToastContainer} from 'react-toastify'
import TitleComponent from '../../components/TitleComponent/TitleComponent'
import LoadingComponent from '../../components/LoadingComponent/LoadingComponent'
import clsx from 'clsx'

import PanelTipDropdown from './components/PanelTipDropdown/PanelTipDropdown'
import PanelIzolacijaDropdown from './components/PanelIzolacijaDropdown/PanelIzolacijaDropdown'
import PanelOblogaDropdown from './components/PanelOblogaDropdown/PanelOblogaDropdown'
import BojaDropdown from '../../components/BojaDropdown/BojaDropdown'
import PanelDebljinaDropdown from './components/PanelDebljinaDropdown/PanelDebljinaDropdown'
import PanelSirinaDropdown from './components/PanelSirinaDropdown/PanelSirinaDropdown'

const NalogDetails: React.FC = () => {
    const {id} = useParams()
    const idFromURL = Number(id)
    const navigate = useNavigate()

    const [loading, setLoading] = useState(true)
    const [initialProizvodnja, setInitialProizvodnja] = useState<Partial<ProizvodnjaModel>>({})
    const [nalogExists, setNalogExists] = useState<boolean | null>(null)
    const [stavkaExists, setStavkaExists] = useState<boolean | null>(null)
    const [checking, setChecking] = useState(false)
    const hasDuplicate = nalogExists === true || stavkaExists === true

    const validationSchema = Yup.object({
        brojNaloga: Yup.string().required(),
        kupac: Yup.string().required(),
        grad: Yup.string().required(),
        adresa: Yup.string().required(),
        drzava: Yup.string().required(),
        kontaktTel: Yup.string().required(),
        tip: Yup.string().required(),
        brtvenaTraka: Yup.number().required(),
        izolacija: Yup.string().required(),
        prepust: Yup.number().required(),
        masina: Yup.number().required(),
        sirinaNalog: Yup.number().required(),
        vanjskaOblogaG: Yup.number().required(),
        bojaG: Yup.string().required(),
        debljinaG: Yup.number().required(),
        folijaG: Yup.number().required(),
        vanjskaOblogaD: Yup.number().required(),
        bojaD: Yup.string().required(),
        debljinaD: Yup.number().required(),
        folijaD: Yup.number().required(),
        kolicina: Yup.number().required(),
        isporukaFMK: Yup.string().required(),
        statusPro: Yup.string().required(),
        statusProDatum: Yup.string().required(),
        status: Yup.number().required(),
    })

    const formik = useFormik<Partial<ProizvodnjaModel>>({
        initialValues: initialProizvodnja,
        enableReinitialize: true,
        validationSchema,
        onSubmit: async (values, {setSubmitting}) => {
            if (hasDuplicate) {
                notifyToast(false, 'Nalog ili stavka već postoji u sistemu.')
                setSubmitting(false)
                return
            }

            setLoading(true)

            try {
                const res = idFromURL
                    ? await updateProizvodnja(idFromURL, values)
                    : await saveProizvodnja(values)

                notifyToast(res.error === 0, res.message)

                if (res.error === 0 && idFromURL) {
                    setTimeout(() => navigate(-1), 1500)
                }
            } catch (e: any) {
                notifyToast(false, e?.response?.data?.message || 'Error')
            } finally {
                setLoading(false)
                setSubmitting(false)
            }
        }
    })

    // LOAD DATA + INITIAL CHECK
    useEffect(() => {
        if (idFromURL) {
            fetchProizvodnja(idFromURL)
                .then((data) => {
                    setInitialProizvodnja(data)

                    if (data?.brojNaloga) {
                        const numericStavka =
                            data.stavka !== null &&
                            data.stavka !== undefined &&
                            data.stavka !== ''
                                ? Number(data.stavka)
                                : undefined

                        checkNalog(data.brojNaloga, numericStavka, idFromURL)
                    }
                })
                .finally(() => setLoading(false))
        } else {
            setLoading(false)
        }
    }, [idFromURL])

    // CHECK FUNCTION
    const checkNalog = async (
        brojNaloga: string,
        stavka?: number,
        idFromURL?: number
    ) => {
        try {
            setChecking(true)

            // reset UI state
            setNalogExists(null)
            setStavkaExists(null)

            const res = await postojiLiNalogUpisan(
                brojNaloga,
                stavka,
                idFromURL
            )

            if (stavka === null || stavka === undefined) {
                setNalogExists(res.exists)
            } else {
                setStavkaExists(res.exists)
            }
        } finally {
            setChecking(false)
        }
    }

    const input = (name: any) =>
        clsx('form-control form-control-lg form-control-solid', {
            'is-invalid': formik.touched[name] && formik.errors[name],
        })

    const showError = (name: any) => formik.touched[name] && formik.errors[name]
    const errorText = (name: any) => formik.errors[name]

    return (
        <div className='card shadow-sm'>
            <ToastContainer/>

            <div className='card-header border-0'>
                <div className='card-title m-0'>
                    <TitleComponent name='Proizvodnja'/>
                </div>
            </div>

            {loading ? (
                <LoadingComponent/>
            ) : (
                <form onSubmit={formik.handleSubmit} className='form'>
                    <div className='card-body p-9'>

                        {/* FIRST ROW */}
                        <div className='row g-5 mb-8'>
                            <div className='col-lg-4'>
                                <label className='form-label fw-bold'>Broj naloga</label>
                                <input disabled value={formik.values.brojNaloga ?? ''} className={input('brojNaloga')}/>
                            </div>

                            <div className='col-lg-4'>
                                <label className='form-label fw-bold'>Broj ponude</label>
                                <input disabled value={formik.values.brojPonude ?? ''} className={input('brojPonude')}/>
                            </div>

                            <div className='col-lg-4'>
                                <label className='form-label fw-bold'>Stavka</label>
                                <input
                                    type='number'
                                    name='stavka'
                                    value={formik.values.stavka ?? ''}
                                    className={input('stavka')}
                                    onChange={(e) => {
                                        formik.handleChange(e)

                                        const brojNaloga = formik.values.brojNaloga
                                        const value = e.target.value

                                        if (!brojNaloga) return

                                        const numericStavka =
                                            value !== '' ? Number(value) : undefined

                                        checkNalog(brojNaloga, numericStavka, idFromURL)
                                    }}
                                />
                            </div>
                        </div>

                        {/* ALERTS */}
                        {checking && <div className='alert alert-warning mb-6'>Provjera...</div>}

                        {!checking && nalogExists === true && stavkaExists === null && (
                            <div className='alert alert-danger mb-6'>
                                Nalog postoji razrađen u sistemu. Ako nalog nema stavku, ne može biti snimljen.
                            </div>
                        )}

                        {!checking && stavkaExists === true && (
                            <div className='alert alert-danger mb-6'>
                                Stavka već postoji za ovaj nalog
                            </div>
                        )}

                        {!checking && stavkaExists === false && (
                            <div className='alert alert-success mb-6'>
                                Nalog se može upisati
                            </div>
                        )}
                        {/* OSNOVNO */}
                        <div className='mb-10'>
                            <div className='d-flex align-items-center mb-5'>
                                <div className='fs-4 fw-bold text-gray-900'>Osnovni podaci</div>
                            </div>
                            <div className='separator separator-dashed mb-6'></div>

                            <div className='row g-5'>
                                <div className='col-lg-4 col-md-6'>
                                    <label className='form-label required fw-bold fs-6'>Kupac</label>
                                    <input type='text' {...formik.getFieldProps('kupac')} className={input('kupac')}
                                           placeholder='Kupac'/>
                                    {showError('kupac') && <div className='fv-help-block'>{errorText('kupac')}</div>}
                                </div>

                                <div className='col-lg-4 col-md-6'>
                                    <label className='form-label required fw-bold fs-6'>Grad</label>
                                    <input type='text' {...formik.getFieldProps('grad')} className={input('grad')}
                                           placeholder='Grad'/>
                                    {showError('grad') && <div className='fv-help-block'>{errorText('grad')}</div>}
                                </div>

                                <div className='col-lg-4 col-md-6'>
                                    <label className='form-label required fw-bold fs-6'>Adresa - Grad</label>
                                    <input
                                        type='text'
                                        {...formik.getFieldProps('adresa')}
                                        className={input('adresa')}
                                        placeholder='Adresa - Grad'
                                    />
                                    {showError('adresa') && <div className='fv-help-block'>{errorText('adresa')}</div>}
                                </div>

                                <div className='col-lg-4 col-md-6'>
                                    <label className='form-label required fw-bold fs-6'>Država</label>
                                    <input
                                        type='text'
                                        {...formik.getFieldProps('drzava')}
                                        className={input('drzava')}
                                        placeholder='Država'
                                    />
                                    {showError('drzava') && <div className='fv-help-block'>{errorText('drzava')}</div>}
                                </div>

                                <div className='col-lg-4 col-md-6'>
                                    <label className='form-label required fw-bold fs-6'>Kontakt tel</label>
                                    <input
                                        type='text'
                                        {...formik.getFieldProps('kontaktTel')}
                                        className={input('kontaktTel')}
                                        placeholder='Kontakt tel'
                                    />
                                    {showError('kontaktTel') &&
                                        <div className='fv-help-block'>{errorText('kontaktTel')}</div>}
                                </div>

                                <div className='col-lg-4 col-md-6'>
                                    <label className='form-label fw-bold fs-6'>Krajnji kupac</label>
                                    <input
                                        type='text'
                                        {...formik.getFieldProps('krajnjiKupac')}
                                        className={input('krajnjiKupac')}
                                        placeholder='Krajnji kupac'
                                    />
                                    {showError('krajnjiKupac') &&
                                        <div className='fv-help-block'>{errorText('krajnjiKupac')}</div>}
                                </div>
                            </div>
                        </div>

                        {/* PANEL */}
                        <div className='mb-10'>
                            <div className='d-flex align-items-center mb-5'>
                                <div className='fs-4 fw-bold text-gray-900'>Podaci o panelu</div>
                            </div>
                            <div className='separator separator-dashed mb-6'></div>

                            <div className='row g-5'>
                                <div className='col-lg-3 col-md-6'>
                                    <label className='form-label required fw-bold fs-6'>Tip panela</label>
                                    <PanelTipDropdown
                                        value={formik.values.tip}
                                        onChange={(_, value) => formik.setFieldValue('tip', value)}
                                        errors={(formik.errors as any).tip}
                                        touched={(formik.touched as any).tip}
                                        selectName='tip'
                                    />
                                </div>

                                <div className='col-lg-3 col-md-6'>
                                    <label className='form-label required fw-bold fs-6'>Panel izolacija</label>
                                    <PanelIzolacijaDropdown
                                        value={formik.values.izolacija}
                                        onChange={(_, value) => formik.setFieldValue('izolacija', value)}
                                        errors={(formik.errors as any).izolacija}
                                        touched={(formik.touched as any).izolacija}
                                        selectName='izolacija'
                                    />
                                </div>

                                <div className='col-lg-3 col-md-6'>
                                    <label className='form-label required fw-bold fs-6'>Širina panela</label>
                                    <PanelSirinaDropdown
                                        value={formik.values.sirinaNalog}
                                        onChange={(_, value) => formik.setFieldValue('sirinaNalog', value)}
                                        errors={(formik.errors as any).sirinaNalog}
                                        touched={(formik.touched as any).sirinaNalog}
                                        selectName='sirina'
                                    />
                                </div>

                                <div className='col-lg-3 col-md-6'>
                                    <label className='form-label required fw-bold fs-6'>Linija</label>
                                    <select {...formik.getFieldProps('masina')} className={input('masina')}>
                                        <option value=''>Odaberi...</option>
                                        <option value='1'>Linija 1</option>
                                        <option value='2'>Linija 2</option>
                                    </select>
                                    {showError('masina') && <div className='fv-help-block'>{errorText('masina')}</div>}
                                </div>
                                <div className='col-lg-3 col-md-6'>
                                    <label className='form-label fw-bold fs-6'>Artikal</label>
                                    <input
                                        type='text'
                                        {...formik.getFieldProps('artikal')}
                                        className={input('artikal')}
                                        placeholder='Artikal'
                                    />
                                    {showError('artikal') &&
                                        <div className='fv-help-block'>{errorText('artikal')}</div>}
                                </div>

                                <div className='col-lg-3 col-md-6'>
                                    <label className='form-label required fw-bold fs-6'>Prepust</label>
                                    <select {...formik.getFieldProps('prepust')} className={input('prepust')}>
                                        <option value=''>Odaberi...</option>
                                        <option value='0'>Ne</option>
                                        <option value='1'>Da</option>
                                    </select>
                                    {showError('prepust') &&
                                        <div className='fv-help-block'>{errorText('prepust')}</div>}
                                </div>

                                <div className='col-lg-3 col-md-6'>
                                    <label className='form-label fw-bold fs-6'>Dužina prepusta</label>
                                    <input
                                        type='number'
                                        {...formik.getFieldProps('duzinaPrepusta')}
                                        className={input('duzinaPrepusta')}
                                        placeholder='Dužina prepusta'
                                    />
                                    {showError('duzinaPrepusta') &&
                                        <div className='fv-help-block'>{errorText('duzinaPrepusta')}</div>}
                                </div>

                                <div className='col-lg-3 col-md-6'>
                                    <label className='form-label required fw-bold fs-6'>Brtvena traka</label>
                                    <select {...formik.getFieldProps('brtvenaTraka')} className={input('brtvenaTraka')}>
                                        <option value=''>Odaberi...</option>
                                        <option value='0'>Ne</option>
                                        <option value='1'>Da</option>
                                    </select>
                                    {showError('brtvenaTraka') &&
                                        <div className='fv-help-block'>{errorText('brtvenaTraka')}</div>}
                                </div>
                            </div>
                        </div>

                        {/* LIMOVI */}
                        <div className='mb-10'>
                            <div className='d-flex align-items-center mb-5'>
                                <div className='fs-4 fw-bold text-gray-900'>Limovi</div>
                            </div>
                            <div className='separator separator-dashed mb-6'></div>

                            <div className='row g-6'>
                                <div className='col-lg-6'>
                                    <div className='card border border-gray-300 h-100 shadow-sm'>
                                        <div className='card-header py-4 min-h-auto'>
                                            <h4 className='card-title fw-bold text-primary m-0'>Gornji lim (Lice)</h4>
                                        </div>

                                        <div className='card-body'>
                                            <div className='row g-5'>
                                                <div className='col-12'>
                                                    <label className='form-label required fw-bold fs-6'>Vanjska
                                                        obloga</label>
                                                    <PanelOblogaDropdown
                                                        value={formik.values.vanjskaOblogaG}
                                                        onChange={(_, value) => formik.setFieldValue('vanjskaOblogaG', value)}
                                                        errors={(formik.errors as any).vanjskaOblogaG}
                                                        touched={(formik.touched as any).vanjskaOblogaG}
                                                        selectName='vanjskaOblogaG'
                                                    />
                                                </div>

                                                <div className='col-12'>
                                                    <label className='form-label required fw-bold fs-6'>Boja</label>
                                                    <BojaDropdown
                                                        value={formik.values.bojaG}
                                                        onChange={(_, value) => formik.setFieldValue('bojaG', value)}
                                                        errors={(formik.errors as any).bojaG}
                                                        touched={(formik.touched as any).bojaG}
                                                        selectName='bojaG'
                                                    />
                                                </div>

                                                <div className='col-12'>
                                                    <label className='form-label required fw-bold fs-6'>Debljina</label>
                                                    <PanelDebljinaDropdown
                                                        value={formik.values.debljinaG}
                                                        onChange={(_, value) => formik.setFieldValue('debljinaG', value)}
                                                        errors={(formik.errors as any).debljinaG}
                                                        touched={(formik.touched as any).debljinaG}
                                                        selectName='debljinaG'
                                                    />
                                                </div>

                                                <div className='col-12'>
                                                    <label className='form-label fw-bold fs-6'>Broj rolne</label>
                                                    <input
                                                        type='text'
                                                        {...formik.getFieldProps('rolneG')}
                                                        className={input('rolneG')}
                                                        placeholder='Unesite broj rolne'
                                                    />
                                                    {showError('rolneG') &&
                                                        <div className='fv-help-block'>{errorText('rolneG')}</div>}
                                                </div>

                                                <div className='col-12'>
                                                    <label className='form-label required fw-bold fs-6'>Folija</label>
                                                    <select {...formik.getFieldProps('folijaG')}
                                                            className='form-select form-select-lg form-select-solid'>
                                                        <option value=''>Odaberi...</option>
                                                        <option value='0'>Ne</option>
                                                        <option value='1'>Da</option>
                                                    </select>
                                                    {showError('folijaG') &&
                                                        <div className='fv-help-block'>{errorText('folijaG')}</div>}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className='col-lg-6'>
                                    <div className='card border border-gray-300 h-100 shadow-sm'>
                                        <div className='card-header py-4 min-h-auto'>
                                            <h4 className='card-title fw-bold text-success m-0'>Donji lim (Naličje)</h4>
                                        </div>

                                        <div className='card-body'>
                                            <div className='row g-5'>
                                                <div className='col-12'>
                                                    <label className='form-label required fw-bold fs-6'>Vanjska
                                                        obloga</label>
                                                    <PanelOblogaDropdown
                                                        value={formik.values.vanjskaOblogaD}
                                                        onChange={(_, value) => formik.setFieldValue('vanjskaOblogaD', value)}
                                                        errors={(formik.errors as any).vanjskaOblogaD}
                                                        touched={(formik.touched as any).vanjskaOblogaD}
                                                        selectName='vanjskaOblogaD'
                                                    />
                                                </div>

                                                <div className='col-12'>
                                                    <label className='form-label required fw-bold fs-6'>Boja</label>
                                                    <BojaDropdown
                                                        value={formik.values.bojaD}
                                                        onChange={(_, value) => formik.setFieldValue('bojaD', value)}
                                                        errors={(formik.errors as any).bojaD}
                                                        touched={(formik.touched as any).bojaD}
                                                        selectName='bojaD'
                                                    />
                                                </div>

                                                <div className='col-12'>
                                                    <label className='form-label required fw-bold fs-6'>Debljina</label>
                                                    <PanelDebljinaDropdown
                                                        value={formik.values.debljinaD}
                                                        onChange={(_, value) => formik.setFieldValue('debljinaD', value)}
                                                        errors={(formik.errors as any).debljinaD}
                                                        touched={(formik.touched as any).debljinaD}
                                                        selectName='debljinaD'
                                                    />
                                                </div>

                                                <div className='col-12'>
                                                    <label className='form-label fw-bold fs-6'>Broj rolne</label>
                                                    <input
                                                        type='text'
                                                        {...formik.getFieldProps('rolneD')}
                                                        className={input('rolneD')}
                                                        placeholder='Unesite broj rolne'
                                                    />
                                                    {showError('rolneD') &&
                                                        <div className='fv-help-block'>{errorText('rolneD')}</div>}
                                                </div>

                                                <div className='col-12'>
                                                    <label className='form-label required fw-bold fs-6'>Folija</label>
                                                    <select {...formik.getFieldProps('folijaD')}
                                                            className='form-select form-select-lg form-select-solid'>
                                                        <option value=''>Odaberi...</option>
                                                        <option value='0'>Ne</option>
                                                        <option value='1'>Da</option>
                                                    </select>
                                                    {showError('folijaD') &&
                                                        <div className='fv-help-block'>{errorText('folijaD')}</div>}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* STATUS / DODATNO */}
                        <div className='mb-0'>
                            <div className='d-flex align-items-center mb-5'>
                                <div className='fs-4 fw-bold text-gray-900'>Status i dodatni podaci</div>
                            </div>
                            <div className='separator separator-dashed mb-6'></div>

                            <div className='row g-5'>
                                <div className='col-lg-3 col-md-6'>
                                    <label className='form-label required fw-bold fs-6'>Količina / m2</label>
                                    <input
                                        type='number'
                                        {...formik.getFieldProps('kolicina')}
                                        className={input('kolicina')}
                                        placeholder='Količina'
                                    />
                                    {showError('kolicina') &&
                                        <div className='fv-help-block'>{errorText('kolicina')}</div>}
                                </div>

                                <div className='col-lg-3 col-md-6'>
                                    <label className='form-label fw-bold fs-6'>Broj panela (kom)</label>
                                    <input
                                        type='number'
                                        {...formik.getFieldProps('brojPanela')}
                                        className={input('brojPanela')}
                                        placeholder='Broj panela'
                                    />
                                    {showError('brojPanela') &&
                                        <div className='fv-help-block'>{errorText('brojPanela')}</div>}
                                </div>

                                <div className='col-lg-3 col-md-6'>
                                    <label className='form-label fw-bold fs-6'>Vrijeme proizvodnje</label>
                                    <input
                                        type='text'
                                        {...formik.getFieldProps('vrijemeProizvodnje')}
                                        className={input('vrijemeProizvodnje')}
                                        placeholder='Vrijeme proizvodnje'
                                    />
                                    {showError('vrijemeProizvodnje') &&
                                        <div className='fv-help-block'>{errorText('vrijemeProizvodnje')}</div>}
                                </div>

                                <div className='col-lg-3 col-md-6'>
                                    <label className='form-label required fw-bold fs-6'>Isporuka FMK</label>
                                    <input
                                        type='date'
                                        {...formik.getFieldProps('isporukaFMK')}
                                        className={input('isporukaFMK')}
                                    />
                                    {showError('isporukaFMK') &&
                                        <div className='fv-help-block'>{errorText('isporukaFMK')}</div>}
                                </div>

                                <div className='col-lg-4 col-md-6'>
                                    <label className='form-label fw-bold fs-6'>Napomena</label>
                                    <input
                                        type='text'
                                        {...formik.getFieldProps('napomena')}
                                        className={input('napomena')}
                                        placeholder='Napomena'
                                    />
                                    {showError('napomena') &&
                                        <div className='fv-help-block'>{errorText('napomena')}</div>}
                                </div>

                                <div className='col-lg-4 col-md-6'>
                                    <label className='form-label required fw-bold fs-6'>Status proizvodnje</label>
                                    <select {...formik.getFieldProps('statusPro')} className={input('statusPro')}>
                                        <option value=''>Odaberi...</option>
                                        <option value='Planirano'>Proizvodnja planirana</option>
                                        <option value='Zavrseno'>Proizvodnja završena</option>
                                    </select>
                                    {showError('statusPro') &&
                                        <div className='fv-help-block'>{errorText('statusPro')}</div>}
                                </div>

                                <div className='col-lg-4 col-md-6'>
                                    <label className='form-label required fw-bold fs-6'>Datum statusa</label>
                                    <input
                                        type='date'
                                        {...formik.getFieldProps('statusProDatum')}
                                        className={input('statusProDatum')}
                                    />
                                    {showError('statusProDatum') &&
                                        <div className='fv-help-block'>{errorText('statusProDatum')}</div>}
                                </div>

                                <div className='col-lg-4 col-md-6'>
                                    <label className='form-label required fw-bold fs-6'>Status</label>
                                    <select {...formik.getFieldProps('status')} className={input('status')}>
                                        <option value=''>Odaberi...</option>
                                        <option value='0'>Aktivan</option>
                                        <option value='1'>Arhiviran</option>
                                    </select>
                                    {showError('status') && <div className='fv-help-block'>{errorText('status')}</div>}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className='card-footer d-flex justify-content-end py-6 px-9'>
                        <button
                            type='submit'
                            className='btn btn-primary px-8'
                            disabled={
                                loading ||
                                formik.isSubmitting ||
                                checking ||
                                hasDuplicate
                            }
                        >
                            {!loading && !formik.isSubmitting && 'Potvrdi'}
                            {(loading || formik.isSubmitting) && (
                                <span className='indicator-progress'>
                                    Please wait...
                                    <span className='spinner-border spinner-border-sm ms-2'></span>
                                </span>
                            )}
                        </button>
                    </div>
                </form>
            )}
        </div>
    )
}

export default NalogDetails